import React, { useLayoutEffect, useMemo } from 'react';
import { toast } from 'react-toastify';

import classNames from 'classnames';

import ImageProxy from '@next-image-proxy';

import { Modal, ModalCloseButton, ModalContent, ModalHeading, ModalProps, Spinner } from '@uikit';

import {
  usePoolWhitelistJoiningCriteriaResultsQuery,
  useVerifySelfPoolWhitelistMutation,
} from '@query';

import { Checked } from '@uikit/icons/Checked';
import { CloseCircle } from '@uikit/icons/CloseCircle';

import { getErrorMessage } from '@utilities/errors/getErrorMessage';

interface PortfolioPoolWhitelistRequirementsModalProps extends Omit<ModalProps, 'children'> {
  poolId: string;
  projectId?: string;
  disabled?: boolean;
}

export const PortfolioPoolWhitelistRequirementsModal = ({
  poolId,
  projectId,
  disabled,
  isOpen,
  ...props
}: PortfolioPoolWhitelistRequirementsModalProps) => {
  const {
    mutateAsync: verifyWhitelist,
    isPending: isVerifyingWhitelist,
    data: verifyWhitelistData,
    reset: resetVerifyWhitelist,
  } = useVerifySelfPoolWhitelistMutation();
  const {
    data: whitelistJoiningCriteriaResults,
    isLoading: areWhitelistJoiningCriteriaResultsLoading,
  } = usePoolWhitelistJoiningCriteriaResultsQuery(poolId, {
    enabled: isOpen,
  });

  useLayoutEffect(() => {
    if (isOpen) {
      resetVerifyWhitelist();
    }
  }, [isOpen, resetVerifyWhitelist]);

  const verifyingCriteriaResults = verifyWhitelistData || whitelistJoiningCriteriaResults;
  const isLoading = areWhitelistJoiningCriteriaResultsLoading || isVerifyingWhitelist;

  const isDisableVerifyButton = useMemo(() => {
    return (
      !!verifyWhitelistData &&
      verifyingCriteriaResults?.criteriaResults?.some((criteria) => !criteria?.verified)
    );
  }, [verifyWhitelistData, verifyingCriteriaResults]);

  const handleVerifyWhitelistButtonClick = async () => {
    // NOTE: reseting to show default state, because state of the cards depends on verifyWhitelistData
    // and if there's no verifyWhitelistData we show default cards
    resetVerifyWhitelist();

    try {
      await verifyWhitelist({ poolId, projectId });
    } catch (error) {
      toast.error(`Unable to verify whitelist. ${getErrorMessage(error)}`);
    }
  };

  const isVerified = !!verifyWhitelistData && verifyWhitelistData.meta.participationVerified;

  const showWhitelistApprovedModal = isVerified;
  const showWhitelistRequiredModal = !isVerified;

  const showJoiningCriteriaResultsSkeleton = areWhitelistJoiningCriteriaResultsLoading;
  const showJoiningCriteriaResults =
    !areWhitelistJoiningCriteriaResultsLoading &&
    !!verifyingCriteriaResults?.criteriaResults.length;

  const showWhitelistApprovedButton = isVerified;
  const showVerifyWhitelistButton = !isVerified;
  const showVerifyWhitelistButtonLoading = isLoading;
  const isVerifyWhitelistButtonDisabled = isLoading || disabled;

  return (
    <Modal isOpen={isOpen} {...props}>
      <ModalContent className="flex flex-col items-center py-6 px-2 sm:px-9 w-full max-w-[472px]">
        <ModalCloseButton className="!mb-0" />

        {showWhitelistRequiredModal && (
          <>
            <ModalHeading className="my-4">Whitelist Requirements</ModalHeading>

            <p className="font-light text-sm text-neutral-500 mb-6 text-center px-5">
              This whitelist pool is privately open for the Magic Launchpad KYC verified user who
              pass certain conditions:
            </p>
          </>
        )}

        {showWhitelistApprovedModal && (
          <>
            <ImageProxy
              alt=""
              src="launchpad/apply.png"
              width={200}
              height={140}
              className="w-[6.25rem] h-[4.375rem] mt-4 mb-3"
            />

            <ModalHeading className="mb-[1.125rem]">Whitelist Approved</ModalHeading>
          </>
        )}

        <div className="mb-6 w-full">
          {showJoiningCriteriaResultsSkeleton && (
            <div className="h-24 bg-[#F1F0F2] animate-pulse rounded-lg" />
          )}

          {showJoiningCriteriaResults && (
            <ul className="flex flex-col gap-2">
              {verifyingCriteriaResults.criteriaResults.map((criteria) => {
                const isDefaultCard = !verifyWhitelistData;
                const isSuccessCard = !!verifyWhitelistData && criteria.verified;
                const isErrorCard = !!verifyWhitelistData && !criteria.verified;

                return (
                  <li
                    key={criteria.whiteListJoiningCriteria.id}
                    className={classNames('flex flex-col gap-1 w-full p-4 rounded-lg', {
                      'bg-[#F1F0F2]': isDefaultCard,
                      'bg-green-50': isSuccessCard,
                      'bg-secondary-200': isErrorCard,
                    })}
                  >
                    <h6 className="font-semibold max-sm:text-center gap-2 flex items-center">
                      {isSuccessCard && <Checked className="icon-sm text-green-900" />}
                      {isErrorCard && <CloseCircle className="icon-sm text-red-600" />}
                      {criteria.whiteListJoiningCriteria.title}
                    </h6>
                    <p className="font-light text-sm text-neutral-500 pr-[3.25rem]">
                      {criteria.whiteListJoiningCriteria.description}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {showVerifyWhitelistButton && (
          <button
            className="button !min-h-11 w-full button-solid-secondary rounded-xl"
            onClick={handleVerifyWhitelistButtonClick}
            disabled={isVerifyWhitelistButtonDisabled || isDisableVerifyButton}
          >
            {showVerifyWhitelistButtonLoading && <Spinner size="sm" color="disabled" />}
            {!showVerifyWhitelistButtonLoading && 'Verify Whitelist'}
          </button>
        )}

        {showWhitelistApprovedButton && (
          <button
            className="button ui-button-solid-green !min-h-11 w-full"
            onClick={() => props.onOpenChange?.(false)}
          >
            Whitelist Approved
          </button>
        )}
      </ModalContent>
    </Modal>
  );
};

export default PortfolioPoolWhitelistRequirementsModal;
